// Realtime-database/Firestore client used by the call flow (call status records read
// by the backend, mobile apps and history views). Messaging/push is staff-side only
// and will be added with the staff softphone (roadmap #41).
import { initializeApp } from 'firebase/app'
import { getDatabase } from 'firebase/database'
import { getFirestore } from 'firebase/firestore'
import { getAuth, signInAnonymously } from 'firebase/auth'
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from 'firebase/app-check'

// Read from build-time environment configuration (roadmap #12), same pattern already used
// below for VITE_FIREBASE_APPCHECK_SITE_KEY. Every value defaults to the current production
// project so a build with no configuration behaves exactly as before this change. This lets
// either a separate dev Firebase project or a namespace inside the current one be selected
// later purely through environment configuration, with no further code change.
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || 'AIzaSyB8muYeXvEQXDuJGHkOPgw9_akeu0BPQTg',
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || 'qr-phone-ef5ce.firebaseapp.com',
  databaseURL:
    import.meta.env.VITE_FIREBASE_DATABASE_URL ||
    'https://qr-phone-ef5ce-default-rtdb.europe-west1.firebasedatabase.app',
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || 'qr-phone-ef5ce',
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || 'qr-phone-ef5ce.appspot.com',
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || '814512766448',
  appId: import.meta.env.VITE_FIREBASE_APP_ID || '1:814512766448:web:6d4e50c1d2e8c463a23135',
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID || 'G-MR0JMG9NXW',
}

export const app = initializeApp(firebaseConfig)

// App Check attestation (roadmap #85), staged but INERT. It only initializes when a site key is
// supplied at build time via VITE_FIREBASE_APPCHECK_SITE_KEY; without it this is a no-op. This
// keeps the console-side rollout (registering the provider, obtaining the site key, then the
// monitored move to enforcement) fully decoupled from the code: nothing here changes behaviour
// until the key is provided, and no site key or debug token is committed. Initialized right after
// the app and before the other services, as the SDK expects.
//
// The provider must match the kind of key registered for this application: the registered key is
// a score-based enterprise key, which is served by a different client library than the classic
// integration and yields no token through it, so the enterprise provider is the correct one.
const appCheckSiteKey = import.meta.env.VITE_FIREBASE_APPCHECK_SITE_KEY
if (appCheckSiteKey) {
  initializeAppCheck(app, {
    provider: new ReCaptchaEnterpriseProvider(appCheckSiteKey),
    isTokenAutoRefreshEnabled: true,
  })
}

export const database = getDatabase(app)
export const firestore = getFirestore(app)
export const auth = getAuth(app)

// Establish an anonymous Firebase authentication session at boot so every realtime-database and
// Firestore operation carries an auth.uid (roadmap #84). Guests are legitimately anonymous and
// staff sign-in goes through the corporate access portal (no Firebase user), so without this the
// call flow would touch the database with no identity at all. The database rules stay open during
// this phase, so a failure here must never block a call: `authReady` always resolves. Callers
// await it before the first database access; unrelated UI is not blocked.
export const authReady: Promise<void> = auth.currentUser
  ? Promise.resolve()
  : signInAnonymously(auth)
      .then(() => undefined)
      .catch(() => undefined)
